import {
  LlmStatus,
  JobCreate, JobResponse, QueueStats, HistoryItem, HealthResponse,
  PermutationList, PermutationListCreate, PermutationListUpdate,
  PermutationListItem, PermutationListItemCreate, PermutationListItemUpdate,
  PermutationPreview, BatchJobCreate, BatchJobResponse,
  PromptBitCategory, PromptBitCategoryCreate, PromptBitCategoryUpdate,
  PromptBit, PromptBitCreate, PromptBitUpdate,
  AIGenerateBitsRequest, AIGenerateBitsResponse,
  AIVariateBitRequest, AIVariateBitResponse,
  AgentChatRequest, AgentChatResponse,
  CollageChatRequest, CollageChatResponse,
  PromptBuild, PromptBuildCreate, PromptBuildUpdate,
  Series, SeriesCreate, SeriesUpdate, JobSeriesUpdate, Pipeline, PipelineRun,
  PipelineParamsResponse, PipelineBatch, PipelineBatchRowInput,
  AIGenerateItemsRequest, AIGenerateItemsResponse,
  AIEnhancePromptRequest, AIEnhancePromptResponse,
  VideoJobCreate, VideoJobResponse, VideoQueueStats,
  Img2ImgJobCreate, Img2ImgJobResponse, Img2ImgQueueStats,
  Pose, PoseExtractRequest, PoseUpdate,
  PoseTransferJobCreate, PoseTransferJobResponse, PoseTransferQueueStats,
  FeedbackType, ImageType, FeedbackCreate, ImageFeedback,
  FeedbackListResponse, FavoritesResponse, BatchDeleteResponse, BatchClearResponse,
  PromptSearchResponse, PromptSearchSort, ListItemUsage,
  VariantSessionCreate, VariantSession, VariantGeneratePromptsResponse,
  VariantPreviewStatusResponse, VariantGenerateFullRequest, VariantGenerateFullResponse,
  VariantSeriesContextResponse, VariantSessionListItem
} from './types';
import { isPrivateModeEnabled } from './privateMode';

function getApiBase(): string {
  // Server-side rendering - use env var
  if (typeof window === 'undefined') {
    return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // Production proxy URLs - map frontend hostname to API hostname
  if (hostname === 'comfy-ui.nichetide.com') {
    return 'https://comfy-api.nichetide.com';
  }

  // Local development
  return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';
}

const API_BASE = getApiBase();

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const apiKey = process.env.NEXT_PUBLIC_API_KEY;
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      // A browser cannot put X-API-Key on `<img src>`, so /outputs/* is guarded by a
      // session cookie rather than left open. The backend returns that cookie on every
      // authenticated response — this line is what lets a cross-origin fetch keep it,
      // and what sends it back afterwards. Images then authenticate themselves.
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(apiKey ? { 'X-API-Key': apiKey } : {}),
        ...(isPrivateModeEnabled() ? { 'X-Private-Mode': '1' } : {}),
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      // Handle FastAPI validation errors (detail is an array) and regular errors (detail is a string)
      let errorMessage: string;
      if (Array.isArray(error.detail)) {
        // Validation error: extract messages from array
        errorMessage = error.detail.map((e: { msg?: string; loc?: string[] }) =>
          e.msg || 'Validation error'
        ).join(', ');
      } else {
        errorMessage = error.detail || `API error: ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Health
  async getHealth(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health');
  }

  // Jobs
  async createJob(job: JobCreate): Promise<JobResponse> {
    return this.request<JobResponse>('/jobs', {
      method: 'POST',
      body: JSON.stringify(job),
    });
  }

  // Save an arbitrary image (e.g. a Pipeline output) into the gallery.
  async saveImage(
    image: string,
    seriesId?: number | null,
    prompt: string = 'Pipeline output',
  ): Promise<{ success: boolean; filename: string; width: number; height: number }> {
    return this.request('/api/images/save', {
      method: 'POST',
      body: JSON.stringify({ image, prompt, series_id: seriesId ?? null, source: 'pipeline' }),
    });
  }

  // Pipelines (node-editor graphs)
  async listPipelines(): Promise<Pipeline[]> {
    return this.request<Pipeline[]>('/pipelines');
  }

  async getPipeline(id: number): Promise<Pipeline> {
    return this.request<Pipeline>(`/pipelines/${id}`);
  }

  async createPipeline(name: string, graphJson: string, description?: string): Promise<Pipeline> {
    return this.request<Pipeline>('/pipelines', {
      method: 'POST',
      body: JSON.stringify({ name, graph_json: graphJson, description: description ?? null }),
    });
  }

  async updatePipeline(
    id: number,
    updates: { name?: string; description?: string; graph_json?: string },
  ): Promise<Pipeline> {
    return this.request<Pipeline>(`/pipelines/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deletePipeline(id: number): Promise<{ deleted: boolean; id: number }> {
    return this.request(`/pipelines/${id}`, { method: 'DELETE' });
  }

  // Pipeline runs (server-side execution)
  async createPipelineRun(graphJson: string, pipelineId?: number | null): Promise<PipelineRun> {
    return this.request<PipelineRun>('/pipelines/runs', {
      method: 'POST',
      body: JSON.stringify({ graph_json: graphJson, pipeline_id: pipelineId ?? null }),
    });
  }

  async getPipelineRun(id: number): Promise<PipelineRun> {
    return this.request<PipelineRun>(`/pipelines/runs/${id}`);
  }

  async listPipelineRuns(limit = 50, offset = 0): Promise<PipelineRun[]> {
    return this.request<PipelineRun[]>(`/pipelines/runs?limit=${limit}&offset=${offset}`);
  }

  // Pipeline params (discovered inputs of a saved pipeline)
  async getPipelineParams(id: number): Promise<PipelineParamsResponse> {
    return this.request<PipelineParamsResponse>(`/pipelines/${id}/params`);
  }

  // Pipeline batches (run a saved pipeline over a table of rows)
  async createPipelineBatch(
    pipelineId: number,
    body: { name?: string | null; rows: PipelineBatchRowInput[]; options?: Record<string, unknown> },
  ): Promise<PipelineBatch> {
    return this.request<PipelineBatch>(`/pipelines/${pipelineId}/batches`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getPipelineBatch(id: number): Promise<PipelineBatch> {
    return this.request<PipelineBatch>(`/pipelines/batches/${id}`);
  }

  async listPipelineBatches(limit = 50, offset = 0): Promise<PipelineBatch[]> {
    return this.request<PipelineBatch[]>(`/pipelines/batches?limit=${limit}&offset=${offset}`);
  }

  async cancelPipelineBatch(id: number): Promise<{ cancelled: boolean; id: number }> {
    return this.request(`/pipelines/batches/${id}/cancel`, { method: 'POST' });
  }

  async deletePipelineBatch(id: number): Promise<{ deleted: boolean; id: number }> {
    return this.request(`/pipelines/batches/${id}`, { method: 'DELETE' });
  }

  // Download a batch's outputs as a zip (named by row). Raw fetch — returns a Blob, not JSON.
  async downloadBatchZip(id: number): Promise<Blob> {
    const apiKey = process.env.NEXT_PUBLIC_API_KEY;
    const res = await fetch(`${this.baseUrl}/pipelines/batches/${id}/export`, {
      headers: {
        ...(apiKey ? { 'X-API-Key': apiKey } : {}),
        ...(isPrivateModeEnabled() ? { 'X-Private-Mode': '1' } : {}),
      },
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({ detail: 'Export failed' }));
      throw new Error(e.detail || `Export failed (${res.status})`);
    }
    return res.blob();
  }

  async getJob(jobId: string): Promise<JobResponse> {
    return this.request<JobResponse>(`/jobs/${jobId}`);
  }

  async listJobs(status?: string, limit: number = 50, offset: number = 0, seriesId?: number): Promise<JobResponse[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (seriesId !== undefined) params.set('series_id', seriesId.toString());
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());
    return this.request<JobResponse[]>(`/jobs?${params}`);
  }

  async cancelJob(jobId: string, force: boolean = false): Promise<JobResponse> {
    const params = force ? '?force=true' : '';
    return this.request<JobResponse>(`/jobs/${jobId}${params}`, {
      method: 'DELETE',
    });
  }

  async retryJob(jobId: string): Promise<JobResponse> {
    return this.request<JobResponse>(`/jobs/${jobId}/retry`, {
      method: 'POST',
    });
  }

  async getQueueStats(): Promise<QueueStats> {
    return this.request<QueueStats>('/jobs/stats');
  }

  async clearCompletedJobs(): Promise<{ cleared: number; remaining: number }> {
    return this.request<{ cleared: number; remaining: number }>('/jobs', {
      method: 'DELETE',
    });
  }

  // History
  async getHistory(limit: number = 50, offset: number = 0): Promise<HistoryItem[]> {
    const params = new URLSearchParams();
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());
    return this.request<HistoryItem[]>(`/history?${params}`);
  }

  async clearHistory(): Promise<{ cleared: number; message: string }> {
    return this.request<{ cleared: number; message: string }>('/history', {
      method: 'DELETE',
    });
  }

  async clearHistoryByPeriod(period: 'today' | 'yesterday' | 'all'): Promise<{ cleared: number; period: string; message: string }> {
    return this.request<{ cleared: number; period: string; message: string }>(`/history/period/${period}`, {
      method: 'DELETE',
    });
  }

  async deleteHistoryItem(itemId: number): Promise<{ deleted: boolean; id: number }> {
    return this.request<{ deleted: boolean; id: number }>(`/history/${itemId}`, {
      method: 'DELETE',
    });
  }

  async deleteJobPermanent(jobId: string): Promise<{ deleted: boolean; job_id: string }> {
    return this.request<{ deleted: boolean; job_id: string }>(`/jobs/${jobId}/permanent`, {
      method: 'DELETE',
    });
  }

  // Permutation Lists
  async getPermutationLists(): Promise<PermutationList[]> {
    return this.request<PermutationList[]>('/permutation-lists');
  }

  /** Per-item image counts for one list. Costs a scan per item, so call it per open list. */
  async getListItemUsage(listId: number): Promise<ListItemUsage> {
    return this.request<ListItemUsage>(`/permutation-lists/${listId}/usage`);
  }

  async createPermutationList(list: PermutationListCreate): Promise<PermutationList> {
    return this.request<PermutationList>('/permutation-lists', {
      method: 'POST',
      body: JSON.stringify(list),
    });
  }

  async getPermutationList(listId: number): Promise<PermutationList> {
    return this.request<PermutationList>(`/permutation-lists/${listId}`);
  }

  async updatePermutationList(listId: number, updates: PermutationListUpdate): Promise<PermutationList> {
    return this.request<PermutationList>(`/permutation-lists/${listId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deletePermutationList(listId: number): Promise<{ deleted: boolean; id: number }> {
    return this.request<{ deleted: boolean; id: number }>(`/permutation-lists/${listId}`, {
      method: 'DELETE',
    });
  }

  // Permutation List Items
  async addListItem(listId: number, item: PermutationListItemCreate): Promise<PermutationListItem> {
    return this.request<PermutationListItem>(`/permutation-lists/${listId}/items`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateListItem(listId: number, itemId: number, updates: PermutationListItemUpdate): Promise<PermutationListItem> {
    return this.request<PermutationListItem>(`/permutation-lists/${listId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteListItem(listId: number, itemId: number): Promise<{ deleted: boolean; id: number }> {
    return this.request<{ deleted: boolean; id: number }>(`/permutation-lists/${listId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  // Permutation List Preview Generation
  async generateListPreviews(listId: number, missingOnly: boolean = false): Promise<{ message: string; total_items?: number; status?: unknown }> {
    const params = missingOnly ? '?missing_only=true' : '';
    return this.request<{ message: string; total_items?: number; status?: unknown }>(`/permutation-lists/${listId}/generate-previews${params}`, {
      method: 'POST',
    });
  }

  async getPreviewGenerationStatus(listId: number): Promise<{ status: string; total?: number; completed?: number; current?: string; message?: string }> {
    return this.request<{ status: string; total?: number; completed?: number; current?: string; message?: string }>(`/permutation-lists/${listId}/preview-status`);
  }

  async cancelListPreviews(listId: number): Promise<{ status: string; message?: string }> {
    return this.request<{ status: string; message?: string }>(`/permutation-lists/${listId}/cancel-previews`, {
      method: 'POST',
    });
  }

  async regenerateItemPreview(listId: number, itemId: number): Promise<{ success: boolean; message: string; preview_image: string }> {
    return this.request<{ success: boolean; message: string; preview_image: string }>(`/permutation-lists/${listId}/items/${itemId}/regenerate-preview`, {
      method: 'POST',
    });
  }

  // Permutation Preview and Generation
  async previewPermutations(prompt: string): Promise<PermutationPreview> {
    return this.request<PermutationPreview>('/permutations/preview', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  }

  async generatePermutations(request: BatchJobCreate): Promise<BatchJobResponse> {
    return this.request<BatchJobResponse>('/permutations/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Prompt bit library
  async getPromptBitCategories(): Promise<PromptBitCategory[]> {
    return this.request<PromptBitCategory[]>('/prompt-bits/categories');
  }

  async createPromptBitCategory(data: PromptBitCategoryCreate): Promise<PromptBitCategory> {
    return this.request<PromptBitCategory>('/prompt-bits/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePromptBitCategory(categoryId: number, updates: PromptBitCategoryUpdate): Promise<PromptBitCategory> {
    return this.request<PromptBitCategory>(`/prompt-bits/categories/${categoryId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deletePromptBitCategory(categoryId: number): Promise<{ deleted: boolean; id: number }> {
    return this.request<{ deleted: boolean; id: number }>(`/prompt-bits/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  async getPromptBits(params?: { categoryId?: number; q?: string; includeInactive?: boolean; tags?: string[] }): Promise<PromptBit[]> {
    const searchParams = new URLSearchParams();
    if (params?.categoryId !== undefined) searchParams.set('category_id', params.categoryId.toString());
    if (params?.q) searchParams.set('q', params.q);
    if (params?.includeInactive) searchParams.set('include_inactive', 'true');
    if (params?.tags && params.tags.length > 0) searchParams.set('tags', params.tags.join(','));
    const query = searchParams.toString();
    return this.request<PromptBit[]>(`/prompt-bits${query ? `?${query}` : ''}`);
  }

  async createPromptBit(data: PromptBitCreate): Promise<PromptBit> {
    return this.request<PromptBit>('/prompt-bits', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPromptBit(bitId: number): Promise<PromptBit> {
    return this.request<PromptBit>(`/prompt-bits/${bitId}`);
  }

  async updatePromptBit(bitId: number, updates: PromptBitUpdate): Promise<PromptBit> {
    return this.request<PromptBit>(`/prompt-bits/${bitId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deletePromptBit(bitId: number): Promise<{ deleted: boolean; id: number }> {
    return this.request<{ deleted: boolean; id: number }>(`/prompt-bits/${bitId}`, {
      method: 'DELETE',
    });
  }

  async generateBitPreviews(categoryId: number, missingOnly: boolean = false): Promise<{ message: string; total_items?: number; status?: unknown }> {
    const params = missingOnly ? '?missing_only=true' : '';
    return this.request<{ message: string; total_items?: number; status?: unknown }>(`/prompt-bits/categories/${categoryId}/generate-previews${params}`, {
      method: 'POST',
    });
  }

  async getBitPreviewStatus(categoryId: number): Promise<{ status: string; total?: number; completed?: number; current?: string; message?: string }> {
    return this.request<{ status: string; total?: number; completed?: number; current?: string; message?: string }>(`/prompt-bits/categories/${categoryId}/preview-status`);
  }

  async cancelBitPreviews(categoryId: number): Promise<{ status: string; message?: string }> {
    return this.request<{ status: string; message?: string }>(`/prompt-bits/categories/${categoryId}/cancel-previews`, {
      method: 'POST',
    });
  }

  async regenerateBitPreview(bitId: number): Promise<PromptBit> {
    return this.request<PromptBit>(`/prompt-bits/${bitId}/regenerate-preview`, {
      method: 'POST',
    });
  }

  async aiGenerateBits(request: AIGenerateBitsRequest): Promise<AIGenerateBitsResponse> {
    return this.request<AIGenerateBitsResponse>('/ai/generate-bits', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async aiVariateBit(request: AIVariateBitRequest): Promise<AIVariateBitResponse> {
    return this.request<AIVariateBitResponse>('/ai/variate-bit', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getLlmStatus(): Promise<LlmStatus> {
    return this.request<LlmStatus>('/ai/llm-status');
  }

  /** Collage assistant turn. Works in any mode; the backend clamps the model outside private mode. */
  async collageChat(request: CollageChatRequest): Promise<CollageChatResponse> {
    return this.request<CollageChatResponse>('/ai/collage/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async agentChat(request: AgentChatRequest): Promise<AgentChatResponse> {
    return this.request<AgentChatResponse>('/ai/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Saved prompt builds
  async getPromptBuilds(): Promise<PromptBuild[]> {
    return this.request<PromptBuild[]>('/prompt-builds');
  }

  async createPromptBuild(data: PromptBuildCreate): Promise<PromptBuild> {
    return this.request<PromptBuild>('/prompt-builds', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePromptBuild(buildId: number, updates: PromptBuildUpdate): Promise<PromptBuild> {
    return this.request<PromptBuild>(`/prompt-builds/${buildId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deletePromptBuild(buildId: number): Promise<{ deleted: boolean; id: number }> {
    return this.request<{ deleted: boolean; id: number }>(`/prompt-builds/${buildId}`, {
      method: 'DELETE',
    });
  }

  // Series
  async getSeries(): Promise<Series[]> {
    return this.request<Series[]>('/series');
  }

  async createSeries(series: SeriesCreate): Promise<Series> {
    return this.request<Series>('/series', {
      method: 'POST',
      body: JSON.stringify(series),
    });
  }

  async getSeriesById(seriesId: number): Promise<Series> {
    return this.request<Series>(`/series/${seriesId}`);
  }

  async updateSeries(seriesId: number, updates: SeriesUpdate): Promise<Series> {
    return this.request<Series>(`/series/${seriesId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteSeries(seriesId: number): Promise<{ deleted: boolean; id: number; images_deleted: number }> {
    return this.request<{ deleted: boolean; id: number; images_deleted: number }>(`/series/${seriesId}`, {
      method: 'DELETE',
    });
  }

  async updateJobSeries(jobId: string, seriesId: number | null): Promise<JobResponse> {
    return this.request<JobResponse>(`/jobs/${jobId}/series`, {
      method: 'PATCH',
      body: JSON.stringify({ series_id: seriesId }),
    });
  }

  // Reference images for qwen-image-2.1 edits: upload once, then pass the filename in
  // JobCreate.reference_images. Multipart, so it bypasses request()'s JSON content type.
  async uploadReferenceImage(file: File): Promise<{ filename: string; width: number; height: number }> {
    const apiKey = process.env.NEXT_PUBLIC_API_KEY;
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`${this.baseUrl}/api/images/upload`, {
      method: 'POST',
      body,
      credentials: 'include',
      headers: {
        ...(apiKey ? { 'X-API-Key': apiKey } : {}),
        ...(isPrivateModeEnabled() ? { 'X-Private-Mode': '1' } : {}),
      },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || `Upload failed (${response.status})`);
    }
    return response.json();
  }

  // Image URL helpers
  getImageUrl(filename: string): string {
    return `${this.baseUrl}/outputs/${filename}`;
  }

  /** An uploaded reference (from uploadReferenceImage) rather than a gallery output. */
  getUploadUrl(filename: string): string {
    return `${this.baseUrl}/uploads/${filename}`;
  }

  /** Where a reference lives: uploads are prefixed ref_, everything else is a gallery output. */
  getReferenceUrl(filename: string): string {
    return filename.startsWith('ref_') ? this.getUploadUrl(filename) : this.getThumbnailUrl(filename);
  }

  getThumbnailUrl(filename: string): string {
    return `${this.baseUrl}/outputs/thumb/${filename}`;
  }

  // AI Helper
  async generateListItems(request: AIGenerateItemsRequest): Promise<AIGenerateItemsResponse> {
    return this.request<AIGenerateItemsResponse>('/ai/generate-items', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async enhancePrompt(request: AIEnhancePromptRequest): Promise<AIEnhancePromptResponse> {
    return this.request<AIEnhancePromptResponse>('/ai/enhance-prompt', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Video Jobs
  async createVideoJob(job: VideoJobCreate): Promise<VideoJobResponse> {
    return this.request<VideoJobResponse>('/video/jobs', {
      method: 'POST',
      body: JSON.stringify(job),
    });
  }

  async listVideoJobs(status?: string, limit: number = 50, offset: number = 0): Promise<VideoJobResponse[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());
    return this.request<VideoJobResponse[]>(`/video/jobs?${params}`);
  }

  async getVideoJob(jobId: string): Promise<VideoJobResponse> {
    return this.request<VideoJobResponse>(`/video/jobs/${jobId}`);
  }

  async cancelVideoJob(jobId: string, force: boolean = false): Promise<VideoJobResponse> {
    const params = force ? '?force=true' : '';
    return this.request<VideoJobResponse>(`/video/jobs/${jobId}${params}`, {
      method: 'DELETE',
    });
  }

  async deleteVideoJob(jobId: string): Promise<{ status: string; job_id: string }> {
    return this.request<{ status: string; job_id: string }>(`/video/jobs/${jobId}/delete`, {
      method: 'DELETE',
    });
  }

  async getVideoQueueStats(): Promise<VideoQueueStats> {
    return this.request<VideoQueueStats>('/video/jobs/stats');
  }

  // Save video to gallery
  async saveVideoToGallery(params: {
    video: string;
    prompt: string;
    source_image?: string;
    width: number;
    height: number;
    frames: number;
    fps?: number;
    has_audio?: boolean;
    seed?: number | null;
    inference_time_ms?: number;
    model?: string;
  }): Promise<VideoJobResponse> {
    return this.request<VideoJobResponse>('/video/save', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // Video URL helper
  wsUrl(path: string): string {
    const base = `${this.baseUrl.replace(/^http/, 'ws')}${path}`;
    const key = process.env.NEXT_PUBLIC_API_KEY;
    if (!key) return base;
    return `${base}${path.includes('?') ? '&' : '?'}key=${encodeURIComponent(key)}`;
  }

  getVideoUrl(filename: string): string {
    return `${this.baseUrl}/video/download/${filename}`;
  }

  // Img2Img Jobs
  async createImg2ImgJob(job: Img2ImgJobCreate): Promise<Img2ImgJobResponse> {
    return this.request<Img2ImgJobResponse>('/img2img/jobs', {
      method: 'POST',
      body: JSON.stringify(job),
    });
  }

  async listImg2ImgJobs(status?: string, limit: number = 50, offset: number = 0, seriesId?: number): Promise<Img2ImgJobResponse[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (seriesId !== undefined) params.set('series_id', seriesId.toString());
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());
    return this.request<Img2ImgJobResponse[]>(`/img2img/jobs?${params}`);
  }

  async getImg2ImgJob(jobId: string): Promise<Img2ImgJobResponse> {
    return this.request<Img2ImgJobResponse>(`/img2img/jobs/${jobId}`);
  }

  async cancelImg2ImgJob(jobId: string, force: boolean = false): Promise<Img2ImgJobResponse> {
    const params = force ? '?force=true' : '';
    return this.request<Img2ImgJobResponse>(`/img2img/jobs/${jobId}${params}`, {
      method: 'DELETE',
    });
  }

  async deleteImg2ImgJob(jobId: string): Promise<{ status: string; job_id: string }> {
    return this.request<{ status: string; job_id: string }>(`/img2img/jobs/${jobId}/delete`, {
      method: 'DELETE',
    });
  }

  async getImg2ImgQueueStats(): Promise<Img2ImgQueueStats> {
    return this.request<Img2ImgQueueStats>('/img2img/jobs/stats');
  }

  // Pose Library
  async extractPose(request: PoseExtractRequest): Promise<Pose> {
    return this.request<Pose>('/poses/extract', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async listPoses(favoritesOnly: boolean = false, limit: number = 50, offset: number = 0): Promise<Pose[]> {
    const params = new URLSearchParams();
    if (favoritesOnly) params.set('favorites_only', 'true');
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());
    return this.request<Pose[]>(`/poses?${params}`);
  }

  async getPose(poseId: number): Promise<Pose> {
    return this.request<Pose>(`/poses/${poseId}`);
  }

  async updatePose(poseId: number, updates: PoseUpdate): Promise<Pose> {
    return this.request<Pose>(`/poses/${poseId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deletePose(poseId: number): Promise<{ deleted: boolean; id: number }> {
    return this.request<{ deleted: boolean; id: number }>(`/poses/${poseId}`, {
      method: 'DELETE',
    });
  }

  // Pose image URL helper
  getPoseImageUrl(filename: string): string {
    return `${this.baseUrl}/outputs/poses/${filename}`;
  }

  // Pose Transfer Jobs
  async createPoseTransferJob(job: PoseTransferJobCreate): Promise<PoseTransferJobResponse> {
    return this.request<PoseTransferJobResponse>('/pose-transfer/jobs', {
      method: 'POST',
      body: JSON.stringify(job),
    });
  }

  async listPoseTransferJobs(status?: string, limit: number = 50, offset: number = 0): Promise<PoseTransferJobResponse[]> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());
    return this.request<PoseTransferJobResponse[]>(`/pose-transfer/jobs?${params}`);
  }

  async getPoseTransferJob(jobId: string): Promise<PoseTransferJobResponse> {
    return this.request<PoseTransferJobResponse>(`/pose-transfer/jobs/${jobId}`);
  }

  async cancelPoseTransferJob(jobId: string, force: boolean = false): Promise<PoseTransferJobResponse> {
    const params = force ? '?force=true' : '';
    return this.request<PoseTransferJobResponse>(`/pose-transfer/jobs/${jobId}${params}`, {
      method: 'DELETE',
    });
  }

  async deletePoseTransferJob(jobId: string): Promise<{ status: string; job_id: string }> {
    return this.request<{ status: string; job_id: string }>(`/pose-transfer/jobs/${jobId}/delete`, {
      method: 'DELETE',
    });
  }

  async getPoseTransferQueueStats(): Promise<PoseTransferQueueStats> {
    return this.request<PoseTransferQueueStats>('/pose-transfer/jobs/stats');
  }

  // Image Feedback API
  async setFeedback(data: FeedbackCreate): Promise<ImageFeedback> {
    return this.request<ImageFeedback>('/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getFeedback(imagePath: string): Promise<ImageFeedback | null> {
    try {
      return await this.request<ImageFeedback>(`/feedback/item/${encodeURIComponent(imagePath)}`);
    } catch {
      return null; // Return null if no feedback found (404)
    }
  }

  async removeFeedback(imagePath: string): Promise<{ deleted: boolean; image_path: string }> {
    return this.request<{ deleted: boolean; image_path: string }>(`/feedback/item/${encodeURIComponent(imagePath)}`, {
      method: 'DELETE',
    });
  }

  async listFeedback(params?: {
    feedback?: FeedbackType;
    image_type?: ImageType;
    limit?: number;
    offset?: number;
  }): Promise<FeedbackListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.feedback) searchParams.set('feedback', params.feedback);
    if (params?.image_type) searchParams.set('image_type', params.image_type);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return this.request<FeedbackListResponse>(`/feedback${query ? `?${query}` : ''}`);
  }

  async getFavorites(params?: { limit?: number; offset?: number }): Promise<FavoritesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return this.request<FavoritesResponse>(`/feedback/favorites${query ? `?${query}` : ''}`);
  }

  /**
   * Find completed images whose prompt contains every search term, across all series.
   * `q` is free text split on commas; `terms` are sent verbatim, so a picked
   * permutation-list value keeps its own commas. Private series are included only
   * while private mode is on.
   */
  async searchImagesByPrompt(params: {
    q?: string;
    terms?: string[];
    seriesId?: number | null;
    noSeries?: boolean;
    sort?: PromptSearchSort;
    seed?: number;
    includePreviews?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<PromptSearchResponse> {
    const searchParams = new URLSearchParams();
    if (params.q) searchParams.set('q', params.q);
    for (const term of params.terms ?? []) searchParams.append('term', term);
    if (params.seriesId != null) searchParams.set('series_id', params.seriesId.toString());
    if (params.noSeries) searchParams.set('no_series', 'true');
    if (params.sort && params.sort !== 'newest') searchParams.set('sort', params.sort);
    if (params.seed) searchParams.set('seed', params.seed.toString());
    if (params.includePreviews) searchParams.set('include_previews', 'true');
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.offset) searchParams.set('offset', params.offset.toString());
    return this.request<PromptSearchResponse>(`/images/search?${searchParams.toString()}`);
  }

  async batchDeleteDisliked(imageType?: ImageType): Promise<BatchDeleteResponse> {
    return this.request<BatchDeleteResponse>('/feedback/batch-delete-disliked', {
      method: 'POST',
      body: JSON.stringify({ image_type: imageType }),
    });
  }

  async batchClearFeedback(params?: {
    feedback?: FeedbackType;
    image_type?: ImageType;
  }): Promise<BatchClearResponse> {
    return this.request<BatchClearResponse>('/feedback/batch-clear', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
  }

  // Variant API
  async generateVariantPrompts(request: VariantSessionCreate): Promise<VariantGeneratePromptsResponse> {
    return this.request<VariantGeneratePromptsResponse>('/variants/generate-prompts', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getVariantSession(sessionId: number): Promise<VariantSession> {
    return this.request<VariantSession>(`/variants/sessions/${sessionId}`);
  }

  async getVariantPreviewStatus(sessionId: number): Promise<VariantPreviewStatusResponse> {
    return this.request<VariantPreviewStatusResponse>(`/variants/sessions/${sessionId}/preview-status`);
  }

  async generateVariantFull(sessionId: number, request: VariantGenerateFullRequest): Promise<VariantGenerateFullResponse> {
    return this.request<VariantGenerateFullResponse>(`/variants/sessions/${sessionId}/generate-full`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getVariantSessions(limit: number = 20): Promise<VariantSessionListItem[]> {
    const params = new URLSearchParams();
    params.set('limit', limit.toString());
    return this.request<VariantSessionListItem[]>(`/variants/sessions?${params}`);
  }

  async getSeriesContext(seriesId: number): Promise<VariantSeriesContextResponse> {
    return this.request<VariantSeriesContextResponse>(`/variants/series-context/${seriesId}`);
  }

  async deleteVariantSession(sessionId: number): Promise<{ deleted: boolean; id: number }> {
    return this.request<{ deleted: boolean; id: number }>(`/variants/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }
}

export const api = new ApiClient();
export default api;
